import ListIcon from "@mui/icons-material/List";
import PersonIcon from "@mui/icons-material/Person";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { Button, Title, useGetIdentity, usePermissions } from "react-admin";
import { Link } from "react-router-dom";
import packageJson from "../../package.json";
import { ScmIdentity } from "./types";

const [version, build] = packageJson.version.split("+");

const InfoBox = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 140,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      p: 1.5,
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight="medium">
      {value ?? "—"}
    </Typography>
  </Box>
);

export const ProfilePage = () => {
  const { identity, isPending } = useGetIdentity();
  const { permissions, isPending: permissionsPending } = usePermissions();

  if (isPending || permissionsPending || !identity) return null;

  const profile = identity as ScmIdentity;
  const isAdmin = permissions === "admin" || profile.admin_scm;

  return (
    <>
      <Title title="Meus dados" />
      <Container maxWidth="sm" sx={{ pt: 1, mb: "auto", alignSelf: "center" }}>
        <Card>
          <CardContent>
            <Stack gap={3}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6">Meus dados</Typography>
                <Button
                  component={Link}
                  to="/"
                  label="ra.action.list"
                  startIcon={<ListIcon />}
                />
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={profile.avatar}
                  sx={{ width: 64, height: 64, bgcolor: "primary.main" }}
                >
                  {!profile.avatar && <PersonIcon fontSize="large" />}
                </Avatar>
                <Stack gap={0.5} flex={1}>
                  <Typography variant="h6">{profile.fullName}</Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={isAdmin ? "Administrador SCM" : "Usuário"}
                      color={isAdmin ? "primary" : "default"}
                    />
                    {profile.homologacao && (
                      <Chip size="small" label="Homologação" color="warning" />
                    )}
                    {profile.multi_empresa && (
                      <Chip
                        size="small"
                        label="Multi-empresa"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Stack>
              </Stack>

              <Typography variant="subtitle2" color="text.secondary">
                Sessão
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                <InfoBox label="ID do usuário" value={profile.userID} />
                <InfoBox label="Conta" value={profile.conta} />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                <InfoBox label="Empresa" value={profile.id_empresa} />
                <InfoBox label="Sigla loja" value={profile.sigla_loja} />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                <InfoBox label="Prédio" value={profile.id_predio} />
                <InfoBox label="Nome reduzido" value={profile.nome_reduzido} />
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={1}
              >
                RNGE 4 - SCM v{version}
                {build ? ` (build ${build})` : ""}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

ProfilePage.path = "/profiles";
